/* ------------------------------------------------------------------------------
 *
 *  # Student Activities Management Components
 *
 *  Grid de actividades de estudiantes con filtros por categoría y estado
 *
 * ---------------------------------------------------------------------------- */

import { 
	initGenericDataTable, 
	refreshDataTable, 
	destroyDataTable 
} from '../integrations/datatables.js';

// Variables de estado
let studentActivitiesCallbacks = null;

// Helper functions
function getActivityStatusBadgeClass(estado) {
	switch(estado) {
		case 'Pendiente': return 'bg-warning';
		case 'En Progreso': return 'bg-primary';
		case 'Completado': return 'bg-success';
		case 'Pausado': return 'bg-secondary';
		default: return 'bg-secondary';
	}
}

function getCategoryIcon(tipoCategoria) {
	const icons = {
		'Matemáticas': 'ph-calculator',
		'Lenguaje': 'ph-book-open',
		'Vida Práctica': 'ph-hand-soap',
		'Sensorial': 'ph-eye',
		'Cultura': 'ph-globe'
	};
	return icons[tipoCategoria] || 'ph-circle';
}

function getProgressPercentage(estado) {
	switch(estado) {
		case 'Pendiente': return 0;
		case 'En Progreso': return 50;
		case 'Completado': return 100;
		case 'Pausado': return 25;
		default: return 0;
	}
}

// Función principal de inicialización
export function initStudentActivitiesGrid(actividades) {
	destroyDataTable('studentActivitiesTable');
	
	const tableData = actividades.map(act => [
		generateActivityActions(act.id, act.estadoId, act.estadoNombre),
		generateActivityColumn(act),
		generateCategoryColumn(act),
		generateStatusColumn(act),
		generateProgressColumn(act),
		act.fechaInicio ? new Date(act.fechaInicio).toLocaleDateString('es-ES') : '-'
	]);
	
	const options = {
		data: tableData,
		columns: [
			{ title: 'Acciones', orderable: false, className: 'text-center no-export', width: '100px' },
			{ title: 'Actividad', width: '30%' },
			{ title: 'Categoría', width: '20%' },
			{ title: 'Estado', className: 'text-center', width: '15%' },
			{ title: 'Progreso', className: 'text-center', width: '20%' },
			{ title: 'Fecha Inicio', width: '15%' }
		],
		exportTitle: 'Actividades de Estudiante - Montessori Nova',
		order: [[5, 'desc']], // Orden por fecha inicio descendente
		buttons: [
			{
				extend: 'collection',
				className: 'btn btn-flat-primary btn-labeled btn-labeled-start dropdown-toggle me-3 rounded',
				text: '<span class="btn-labeled-icon bg-primary text-white rounded-start"><i class="ph-export"></i></span>Exportar',
				buttons: [
					{
						extend: 'excel',
						text: '<i class="ph-file-xls me-1"></i>Excel',
						className: 'dropdown-item',
						title: 'Actividades de Estudiante - Montessori Nova',
						exportOptions: {
							columns: [1, 2, 3, 4, 5],
							format: {
								body: function (inner, coldex, rowdex) {
									if (!inner || (typeof inner === 'string' && !inner.includes('<'))) {
										return inner;
									}
									const $el = $(inner);
									const exportText = $el.attr('data-export');
									if (exportText) {
										return exportText;
									}
									if (inner.length <= 0) return inner;
									let el = $.parseHTML(inner);
									let result = '';
									$.each(el, function (index, item) {
										if (item.innerText === undefined) {
											result = result + item.textContent;
										} else {
											result = result + item.innerText;
										}
									});
									return result;
								}
							}
						}
					},
					{
						extend: 'copy',
						text: '<i class="ph-clipboard-text me-1"></i>Copiar',
						className: 'dropdown-item',
						exportOptions: {
							columns: [1, 2, 3, 4, 5],
							format: {
								body: function (inner, coldex, rowdex) {
									if (!inner || (typeof inner === 'string' && !inner.includes('<'))) {
										return inner;
									}
									const $el = $(inner);
									const exportText = $el.attr('data-export');
									if (exportText) {
										return exportText;
									}
									if (inner.length <= 0) return inner;
									let el = $.parseHTML(inner);
									let result = '';
									$.each(el, function (index, item) {
										if (item.innerText === undefined) {
											result = result + item.textContent;
										} else {
											result = result + item.innerText;
										}
									});
									return result;
								}
							}
						}
					}
				]
			}
		]
	};
	
	return initGenericDataTable('studentActivitiesTable', options);
}

function generateActivityActions(actividadId, estadoId, estadoNombre) {
	return `
		<div class="d-inline-flex">
			<a href="javascript:void(0)" class="text-body me-2" 
			   onclick="window.sfBlazorSB.students.activities.updateStatus(${actividadId}, ${estadoId})" 
			   title="Actualizar estado">
				<i class="ph-arrows-clockwise"></i>
			</a>
			<a href="javascript:void(0)" class="text-body" 
			   onclick="window.sfBlazorSB.students.activities.viewDetails(${actividadId})" 
			   title="Ver detalles">
				<i class="ph-info"></i>
			</a>
		</div>
	`;
}

function generateActivityColumn(actividad) {
	const icon = getCategoryIcon(actividad.tipoCategoriaNombre || '');
	const exportText = actividad.nombre;
	
	return `
		<div class="d-flex align-items-center" data-export="${exportText}">
			<i class="${icon} fs-3 me-2 text-primary"></i>
			<div>
				<div class="fw-semibold">${actividad.nombre || 'Sin nombre'}</div>
				${actividad.descripcion ? `<div class="text-muted fs-sm">${actividad.descripcion}</div>` : ''}
			</div>
		</div>
	`;
}

function generateCategoryColumn(actividad) {
	const tipoCategoria = actividad.tipoCategoriaNombre || 'Sin clasificar';
	const categoria = actividad.categoriaNombre || '';
	const subCategoria = actividad.subCategoriaNombre || '';
	const exportText = `${tipoCategoria} - ${categoria}${subCategoria ? ' - ' + subCategoria : ''}`;
	
	return `
		<div data-export="${exportText}">
			<div class="fw-semibold">${tipoCategoria}</div>
			${categoria ? `<div class="text-muted fs-sm">${categoria}</div>` : ''}
			${subCategoria ? `<div class="badge badge-outline bg-secondary mt-1">${subCategoria}</div>` : ''}
		</div>
	`;
}

function generateStatusColumn(actividad) {
	const badgeClass = getActivityStatusBadgeClass(actividad.estadoNombre || '');
	const estadoNombre = actividad.estadoNombre || 'Sin estado';
	return `<span class="badge ${badgeClass}" data-export="${estadoNombre}">${estadoNombre}</span>`;
}

function generateProgressColumn(actividad) {
	const progress = getProgressPercentage(actividad.estadoNombre || '');
	
	return `
		<div class="d-flex align-items-center" data-export="${progress}%">
			<div class="progress flex-fill" style="height: 8px;">
				<div class="progress-bar ${progress === 100 ? 'bg-success' : 'bg-primary'}" 
				     role="progressbar" 
				     style="width: ${progress}%" 
				     aria-valuenow="${progress}" 
				     aria-valuemin="0" 
				     aria-valuemax="100">
				</div>
			</div>
			<span class="ms-2 text-muted fs-sm">${progress}%</span>
		</div>
	`;
}

// Refresh
export function refreshStudentActivitiesGrid(actividades) {
	destroyDataTable('studentActivitiesTable');
	initStudentActivitiesGrid(actividades);
}

// Callbacks
export function setStudentActivitiesCallbacks(dotNetRef) {
	studentActivitiesCallbacks = dotNetRef;
}

// Acciones
export async function updateActivityStatus(actividadId, estadoActual) {
	const { value: nuevoEstado } = await window.swalInit.fire({
		title: 'Actualizar Estado',
		input: 'select',
		inputOptions: {
			'1': 'Pendiente',
			'2': 'En Progreso',
			'3': 'Completado',
			'4': 'Pausado'
		},
		inputValue: estadoActual,
		showCancelButton: true,
		confirmButtonText: 'Actualizar',
		cancelButtonText: 'Cancelar'
	});
	
	if (nuevoEstado && studentActivitiesCallbacks) {
		await studentActivitiesCallbacks.invokeMethodAsync('UpdateActivityStatus', actividadId, parseInt(nuevoEstado));
	}
}

export function viewDetails(actividadId) {
	if (studentActivitiesCallbacks) {
		studentActivitiesCallbacks.invokeMethodAsync('ViewActivityDetails', actividadId);
	}
}

// Filtros
export function applyActivityFilters() {
	const categoriaFilter = document.getElementById('categoriaFilter')?.value || '';
	const estadoFilter = document.getElementById('estadoFilter')?.value || '';
	const globalSearch = document.getElementById('globalSearch')?.value || '';
	
	const table = $('#studentActivitiesTable').DataTable();
	
	// Clear previous searches
	$.fn.dataTable.ext.search.splice(0);
	
	// Add custom searches
	if (categoriaFilter) {
		$.fn.dataTable.ext.search.push(function(settings, data, dataIndex) {
			return data[2].toLowerCase().includes(categoriaFilter.toLowerCase());
		});
	}
	
	if (estadoFilter) {
		$.fn.dataTable.ext.search.push(function(settings, data, dataIndex) {
			return data[3].includes(estadoFilter);
		});
	}
	
	// Apply global search
	table.search(globalSearch).draw();
}

export function clearActivityFilters() {
	const categoriaFilter = document.getElementById('categoriaFilter');
	const estadoFilter = document.getElementById('estadoFilter');
	const globalSearch = document.getElementById('globalSearch');
	
	if (categoriaFilter) categoriaFilter.value = '';
	if (estadoFilter) estadoFilter.value = '';
	if (globalSearch) globalSearch.value = '';
	
	const table = $('#studentActivitiesTable').DataTable();
	$.fn.dataTable.ext.search.splice(0);
	table.search('').draw();
}
